import React, { Children } from 'react';
import { motion } from 'framer-motion';
// --- Animation Variants ---
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
// --- Hero Section ---
export function Hero() {
  return <section className="bg-brand-light py-8 md:py-24 relative overflow-hidden" data-id="element-94">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-95">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-green/10 rounded-full blur-3xl" data-id="element-96"></div>
        <div className="absolute top-40 -left-40 w-72 h-72 bg-brand-green/5 rounded-full blur-3xl" data-id="element-97"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-98">
        <motion.div className="text-center max-w-4xl mx-auto" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={staggerContainer} data-id="element-99">
          <motion.div variants={fadeInUp} className="mb-4 md:mb-6 inline-block" data-id="element-100">
            <span className="bg-white/5 border border-brand-green/30 text-brand-green font-bold px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-xs sm:text-base shadow-sm" data-id="element-101">
              ⚡ La tienda de recursos #1 para instructores
            </span>
          </motion.div>

          <motion.h1 variants={fadeInUp} className="text-3xl sm:text-5xl md:text-6xl font-black text-white leading-tight mb-4 md:mb-6 tracking-tight relative inline-block" data-id="element-102">
            Todo lo que necesitás para dar clases{' '}
            <span className="text-brand-green relative whitespace-nowrap" data-id="element-103">
              increíbles
              <svg className="absolute -bottom-2 left-0 w-full h-3 text-brand-green/30" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-104">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="transparent" strokeLinecap="round" data-id="element-105" />
              </svg>
            </span>{' '}
            ya está acá
          </motion.h1>

          <motion.p variants={fadeInUp} className="text-base sm:text-xl text-brand-gray mb-8 md:mb-14 max-w-3xl mx-auto font-medium leading-relaxed" data-id="element-106">
            Recursos digitales diseñados para instructores de fitness que
            quieren dar clases de calidad, ahorrar tiempo y crecer
            profesionalmente. Descargá, personalizá y usá desde hoy.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-3 sm:gap-8 md:gap-16" data-id="element-107">
            <div className="flex flex-col items-center bg-white/5 backdrop-blur-sm px-4 py-3 sm:px-6 sm:py-4 rounded-2xl border border-white/10 shadow-sm" data-id="element-108">
              <span className="text-2xl sm:text-4xl md:text-5xl font-black text-brand-green mb-1" data-id="element-109">
                +2000
              </span>
              <span className="text-white font-bold text-[10px] sm:text-sm uppercase tracking-wider" data-id="element-110">
                Instructores
              </span>
            </div>
            <div className="flex flex-col items-center bg-white/5 backdrop-blur-sm px-4 py-3 sm:px-6 sm:py-4 rounded-2xl border border-white/10 shadow-sm" data-id="element-111">
              <span className="text-2xl sm:text-4xl md:text-5xl font-black text-brand-green mb-1" data-id="element-112">
                9
              </span>
              <span className="text-white font-bold text-[10px] sm:text-sm uppercase tracking-wider" data-id="element-113">
                Disciplinas
              </span>
            </div>
            <div className="flex flex-col items-center bg-white/5 backdrop-blur-sm px-4 py-3 sm:px-6 sm:py-4 rounded-2xl border border-white/10 shadow-sm" data-id="element-114">
              <span className="text-2xl sm:text-4xl md:text-5xl font-black text-brand-green mb-1" data-id="element-115">
                100%
              </span>
              <span className="text-white font-bold text-[10px] sm:text-sm uppercase tracking-wider" data-id="element-116">
                Digital
              </span>
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="mt-6 md:mt-10" data-id="element-117">
            <a href="#aviso-legal" className="inline-flex items-center gap-1.5 text-[10px] md:text-xs text-gray-500 hover:text-gray-300 transition-colors font-medium tracking-wide group" data-id="element-118">
              <span className="text-xs md:text-sm" data-id="element-119">🔒</span>
              <span className="border-b border-gray-700 group-hover:border-gray-400 transition-colors" data-id="element-120">
                Contenido protegido · Aviso legal
              </span>
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}
// --- Problem Section ---
export function Problem() {
  const problems = [{
    emoji: '😩',
    title: 'Horas perdidas planificando',
    text: 'Pasás más tiempo armando clases que dándolas. El tiempo que invertís en planificar no se ve reflejado en tus honorarios.'
  }, {
    emoji: '😰',
    title: 'No sabés qué ejercicios combinar',
    text: 'Siempre las mismas rutinas. Tus alumnas empiezan a aburrirse y vos sentís que te estancaste profesionalmente.'
  }, {
    emoji: '📚',
    title: 'Recursos dispersos y desorganizados',
    text: 'Videos de YouTube, apuntes sueltos, grupos de WhatsApp... Todo desordenado. Nada concreto para trabajar desde ya.'
  }, {
    emoji: '😓',
    title: 'Cansancio y falta de motivación',
    text: 'Después de dar varias clases al día, no te queda energía para pensar en la siguiente. Necesitás herramientas que te simplifiquen el día a día.'
  }];
  return <section className="bg-brand-light py-6 md:py-20 border-t border-brand-border/50 relative" data-id="element-121">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-122">
        <motion.div className="text-center mb-5 md:mb-12" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={staggerContainer} data-id="element-123">
          <motion.span variants={fadeInUp} className="text-red-500 font-bold uppercase tracking-wider text-xs md:text-sm mb-1 md:mb-2 block" data-id="element-124">
            ¿Te suena conocido?
          </motion.span>
          <motion.h2 variants={fadeInUp} className="text-xl sm:text-3xl md:text-5xl font-black text-white mb-2 md:mb-4" data-id="element-125">
            Enseñar es tu pasión,
            <br className="hidden sm:block" data-id="element-126" /> pero la preparación{' '}
            <span className="text-red-500" data-id="element-127">te consume</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-sm sm:text-base md:text-lg text-brand-gray max-w-2xl mx-auto font-medium" data-id="element-128">
            Sabemos lo que se siente pasar horas planificando clases cuando lo
            que querés es estar disfrutando lo que hacés.
          </motion.p>
        </motion.div>

        <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-6 mb-6 md:mb-16" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={staggerContainer} data-id="element-129">
          {problems.map((prob, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-[#1A1A1A] p-3 md:p-6 rounded-xl md:rounded-2xl shadow-sm border-l-4 border-l-red-500 border-y border-r border-brand-border hover:shadow-md transition-all flex flex-col justify-center" data-id="element-130">
              <div className="flex items-center gap-2 md:gap-3 mb-1.5 md:mb-3" data-id="element-131">
                <div className="text-xl md:text-3xl bg-red-500/10 w-8 h-8 md:w-12 md:h-12 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-132">
                  {prob.emoji}
                </div>
                <h3 className="text-sm md:text-lg font-bold text-white leading-tight" data-id="element-133">
                  {prob.title}
                </h3>
              </div>
              <p className="text-brand-gray text-xs md:text-sm leading-relaxed pl-10 md:pl-15" data-id="element-134">
                {prob.text}
              </p>
            </motion.div>)}
        </motion.div>

        <motion.div className="text-center bg-gradient-to-br from-[#1A1A1A] to-brand-green/10 p-4 md:p-10 rounded-2xl md:rounded-3xl shadow-md border border-brand-green/20 max-w-3xl mx-auto relative overflow-hidden" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={staggerContainer} data-id="element-135">
          <motion.span variants={fadeInUp} className="bg-brand-green text-white font-bold px-3 py-1 md:px-4 md:py-1.5 rounded-full text-xs md:text-sm inline-block mb-2 md:mb-4 shadow-sm" data-id="element-136">
            La solución está acá 👇
          </motion.span>
          <motion.h3 variants={fadeInUp} className="text-lg sm:text-2xl md:text-3xl font-black text-white mb-1.5 md:mb-3" data-id="element-137">
            Recursos listos para usar hoy mismo
          </motion.h3>
          <motion.p variants={fadeInUp} className="text-brand-gray text-xs sm:text-sm md:text-lg font-medium" data-id="element-138">
            Cada producto es una solución completa que podés descargar ahora y
            aplicar en tu próxima clase.
          </motion.p>
        </motion.div>
      </div>
    </section>;
}
// --- Catalog Section ---
export function Catalog() {
  const products = [{
    id: 'stretching',
    name: 'STRETCHING RESUELTO',
    badge: '⭐ TOP VENTAS',
    gradient: 'bg-gradient-to-br from-slate-900 to-blue-900',
    emoji: '🧘‍♀️',
    banner: 'https://cdn.magicpatterns.com/uploads/duB85Rkd3MkpLwi5Ua8ygZ/Stretching_banner.png',
    desc: 'La guía completa para planificar tus clases de elongación con fundamento y variedad.',
    tags: ['Flexibilidad', 'Elongación', 'Descarga digital'],
    link: 'https://stretching.recursosfitness.shop/'
  }, {
    id: 'pilates',
    name: 'PILATES 360',
    badge: '🔥 NUEVO',
    gradient: 'bg-gradient-to-br from-violet-950 to-purple-800',
    emoji: '🌀',
    banner: 'https://cdn.magicpatterns.com/uploads/qatkZ7J1EaGgJ23rowAFcb/pilates_banner.png',
    desc: 'Todo lo que una instructora de Pilates necesita para dar clases completas, variadas y con bases sólidas.',
    tags: ['Pilates', 'Core', 'Planificación'],
    link: 'https://pilates.rincondocente360.com/'
  }, {
    id: 'gap',
    name: 'GAP 360',
    badge: '🆕 DISPONIBLE',
    gradient: 'bg-gradient-to-br from-slate-800 to-blue-700',
    emoji: '🏋️‍♀️',
    banner: 'https://cdn.magicpatterns.com/uploads/c51zZeY2mi2SQGamZeNrrM/GAP_BANNER.png',
    desc: 'Recursos completos para clases de Glúteos, Abdomen y Piernas. Progresiones listas para usar.',
    tags: ['Glúteos', 'Abdomen', 'Piernas'],
    link: 'https://gap.rincondocente360.com/'
  }, {
    id: 'funcional',
    name: 'FUNCIONAL & CROSS 360',
    badge: '⚡ POPULAR',
    gradient: 'bg-gradient-to-br from-red-950 to-red-800',
    emoji: '🔥',
    banner: 'https://cdn.magicpatterns.com/uploads/gr8UcLRCzdntWorPHLuYXf/banner_funcional.png',
    desc: 'El recurso definitivo para instructoras de entrenamiento funcional. Circuitos y progresiones.',
    tags: ['Funcional', 'Circuitos', 'HIIT'],
    link: 'https://funcional.rincondocente360.com/'
  }, {
    id: 'pole',
    name: 'POLE RESUELTO',
    badge: '✨ ÚNICO',
    gradient: 'bg-gradient-to-br from-violet-950 to-fuchsia-800',
    emoji: '💃',
    banner: 'https://cdn.magicpatterns.com/uploads/aRZSrfmZqckJCpDS19zvgH/pole_banner.png',
    desc: 'Recursos exclusivos para instructoras de Pole Dance. Progresiones por nivel y planificación.',
    tags: ['Pole Dance', 'Aerial', 'Niveles'],
    link: 'https://pole.recursosfitness.shop/'
  }, {
    id: 'telas',
    name: 'TELAS RESUELTO',
    badge: '🌟 ESPECIAL',
    gradient: 'bg-gradient-to-br from-teal-950 to-teal-700',
    emoji: '🎪',
    banner: 'https://cdn.magicpatterns.com/uploads/9YZX5WS7oSKX8fHBvzUFR6/telas_banner.png',
    desc: 'Todo para enseñar Aerial Silks. Progresiones seguras, nomenclatura y clases completas.',
    tags: ['Telas Aéreas', 'Aerial', 'Progresiones'],
    link: 'https://telas.recursosfitness.shop/'
  }, {
    id: 'futbol',
    name: 'FÚTBOL FEMENINO RESUELTO',
    badge: '⚽ EXCLUSIVO',
    gradient: 'bg-gradient-to-br from-green-950 to-lime-900',
    emoji: '⚽',
    banner: 'https://cdn.magicpatterns.com/uploads/8EjFzevBs2WWyJX2DvR5Qx/futbol_fem_banner.png',
    desc: 'Recursos técnicos y físicos para entrenadores de fútbol femenino. Planificaciones específicas.',
    tags: ['Fútbol', 'Femenino', 'Técnica'],
    link: 'https://futbolfem.recursosfitness.shop/'
  }, {
    id: 'atletismo',
    name: 'ATLETISMO RESUELTO',
    badge: '🏃 NUEVO',
    gradient: 'bg-gradient-to-br from-orange-950 to-amber-800',
    emoji: '🏃‍♀️',
    banner: 'https://cdn.magicpatterns.com/uploads/cWv4F6cWAbSmhMengU1ZtH/atletismo_banner.png',
    desc: 'Recursos completos para entrenadores de atletismo. Planificaciones, técnica y progresiones.',
    tags: ['Atletismo', 'Técnica', 'Progresiones'],
    link: 'https://atletismo.recursosfitness.shop/'
  }, {
    id: 'aquagym',
    name: 'AQUAGYM RESUELTO',
    badge: '🏊 NUEVO',
    gradient: 'bg-gradient-to-br from-cyan-950 to-cyan-700',
    emoji: '🏊‍♀️',
    banner: 'https://cdn.magicpatterns.com/uploads/jZWkXYWaNUj5LcVjV7xrpA/Banner_aquagym.png',
    desc: 'Sistema completo para instructores de Aquagym: Planificación, Coreografía, Seguridad y Negocio.',
    tags: ['Aquagym', 'Planificación', 'Coreografía'],
    link: 'https://aquagym.recursosfitness.shop/'
  }];
  return <section id="productos" className="bg-brand-black py-14 md:py-24" data-id="element-139">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-140">
        <motion.div className="text-center mb-10 md:mb-16" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={staggerContainer} data-id="element-141">
          <motion.span variants={fadeInUp} className="text-brand-green font-bold uppercase tracking-wider text-sm mb-2 block" data-id="element-142">
            Nuestros Productos
          </motion.span>
          <motion.h2 variants={fadeInUp} className="text-2xl sm:text-4xl md:text-5xl font-black text-white mb-4 md:mb-6" data-id="element-143">
            Elegí tu disciplina
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-base sm:text-lg text-brand-gray max-w-2xl mx-auto font-medium" data-id="element-144">
            Hacé clic en cualquier producto para ver todos los detalles y
            acceder a la página de compra
          </motion.p>
        </motion.div>

        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={staggerContainer} data-id="element-145">
          {products.map(prod => <motion.a key={prod.id} href={prod.link} target="_blank" rel="noopener noreferrer" variants={fadeInUp} className="group flex flex-col bg-[#1A1A1A] rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-brand-border hover:-translate-y-1" data-id="element-146">
              {/* Banner / Visual */}
              {prod.banner ? <div className="relative overflow-hidden aspect-[4/3]" data-id="element-147">
                  <img src={prod.banner} alt={prod.name} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" data-id="element-148" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-149" />
                  <div className="absolute top-3 right-3 bg-black/40 backdrop-blur-sm text-white text-xs font-bold px-2.5 py-0.5 rounded-full" data-id="element-150">
                    {prod.badge}
                  </div>
                  <div className="absolute bottom-3 left-3 right-3" data-id="element-151">
                    <h3 className="text-lg md:text-xl font-black text-white leading-tight drop-shadow-lg" data-id="element-152">
                      {prod.name}
                    </h3>
                  </div>
                </div> : <div className={`${prod.gradient} relative flex flex-col items-center justify-center aspect-[4/3] text-center overflow-hidden`} data-id="element-153">
                  <div className="absolute top-3 right-3 bg-white/20 backdrop-blur-sm text-white text-xs font-bold px-2.5 py-0.5 rounded-full" data-id="element-154">
                    {prod.badge}
                  </div>
                  <div className="text-5xl md:text-6xl mb-3 group-hover:scale-110 transition-transform duration-300" data-id="element-155">
                    {prod.emoji}
                  </div>
                  <h3 className="text-lg md:text-xl font-black text-white leading-tight px-4" data-id="element-156">
                    {prod.name}
                  </h3>
                  <span className="text-white/50 text-xs font-medium tracking-wider mt-1" data-id="element-157">
                    recursosfitness.shop
                  </span>
                </div>}

              {/* Compact Info */}
              <div className="p-3 md:p-4 flex flex-col gap-2 md:gap-3" data-id="element-158">
                <p className="text-brand-gray text-xs md:text-sm leading-relaxed line-clamp-2" data-id="element-159">
                  {prod.desc}
                </p>
                <button className="w-full bg-brand-green text-white font-bold py-2.5 md:py-3 rounded-xl group-hover:bg-green-600 transition-colors text-sm md:text-base" data-id="element-160">
                  Quiero este 💪
                </button>
              </div>
            </motion.a>)}
        </motion.div>
      </div>
    </section>;
}
// --- Benefits Section ---
export function Benefits() {
  const benefits = [{
    icon: '⚡',
    title: 'Acceso Inmediato',
    text: 'Descargá al instante después de tu compra. Sin esperas.'
  }, {
    icon: '🎯',
    title: '100% Aplicable',
    text: 'Contenido práctico listo para tu próxima clase.'
  }, {
    icon: '💡',
    title: 'Con Fundamento',
    text: 'Basado en evidencia real, no improvisación.'
  }, {
    icon: '🔄',
    title: 'Actualizaciones Incluidas',
    text: 'Recibís mejoras futuras sin costo extra.'
  }, {
    icon: '📱',
    title: 'Compatible con Todo',
    text: 'Funciona en celular, tablet y PC.'
  }];
  return <section className="bg-brand-light py-5 md:py-20 relative" data-id="element-161">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-162">
        <motion.div className="text-center mb-4 md:mb-16" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={staggerContainer} data-id="element-163">
          <motion.span variants={fadeInUp} className="text-brand-green font-bold uppercase tracking-wider text-xs md:text-sm mb-1 md:mb-2 block" data-id="element-164">
            ¿Por qué elegirnos?
          </motion.span>
          <motion.h2 variants={fadeInUp} className="text-xl sm:text-4xl font-black text-white" data-id="element-165">
            Lo que nos hace diferentes
          </motion.h2>
        </motion.div>

        <motion.div className="flex flex-wrap justify-center gap-2 md:gap-6" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={staggerContainer} data-id="element-166">
          {benefits.map((ben, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-[#1A1A1A] p-2.5 md:p-6 rounded-xl md:rounded-2xl shadow-sm border border-brand-border w-[calc(50%-4px)] sm:w-[calc(50%-12px)] lg:w-[calc(20%-19.2px)] min-w-[140px] text-center hover:-translate-y-1 hover:shadow-lg transition-all relative overflow-hidden group" data-id="element-167">
              <div className="absolute top-0 left-0 w-full h-1 bg-brand-green transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" data-id="element-168"></div>
              <div className="w-9 h-9 md:w-16 md:h-16 mx-auto bg-brand-green/10 rounded-full flex items-center justify-center mb-1.5 md:mb-4 group-hover:bg-brand-green group-hover:text-white transition-colors duration-300" data-id="element-169">
                <div className="text-lg md:text-3xl" data-id="element-170">{ben.icon}</div>
              </div>
              <h3 className="text-xs md:text-lg font-bold text-white mb-0.5 md:mb-2" data-id="element-171">
                {ben.title}
              </h3>
              <p className="text-brand-gray text-[10px] md:text-sm leading-snug md:leading-relaxed" data-id="element-172">
                {ben.text}
              </p>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}
// --- Guarantee Section ---
export function Guarantee() {
  return <section className="bg-brand-black py-10 md:py-24" data-id="element-173">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-174">
        <motion.div className="max-w-[800px] mx-auto bg-gradient-to-br from-brand-green to-green-700 rounded-2xl md:rounded-[2rem] p-6 sm:p-12 text-center shadow-2xl shadow-brand-green/20 relative overflow-hidden text-white" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={fadeInUp} data-id="element-175">
          {/* Decorative circles */}
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-white/10 rounded-full blur-2xl" data-id="element-176"></div>
          <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-white/10 rounded-full blur-2xl" data-id="element-177"></div>

          <div className="relative z-10" data-id="element-178">
            <div className="text-5xl md:text-7xl mb-4 md:mb-6 drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]" data-id="element-179">
              🛡️
            </div>
            <h2 className="text-2xl sm:text-4xl font-black mb-3 md:mb-6" data-id="element-180">
              Satisfacción Garantizada
            </h2>
            <p className="text-sm sm:text-xl text-white/90 font-medium leading-relaxed max-w-2xl mx-auto" data-id="element-181">
              Si el recurso no cumple tus expectativas, te devolvemos el dinero.
              <br data-id="element-182" />
              Sin preguntas, sin vueltas.
              <br data-id="element-183" />
              <strong className="text-white mt-3 block text-base sm:text-2xl" data-id="element-184">
                Tu confianza es nuestra prioridad.
              </strong>
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}
// --- Testimonials Section ---
export function Testimonials() {
  const testimonials = [{
    name: 'Mariana L.',
    info: 'Instructora de Yoga y Stretching · Buenos Aires',
    text: 'Compré Stretching Resuelto y fue un antes y después en mis clases. Tengo toda la información organizada, con fundamento, y mis alumnas notan la diferencia. ¡Vale cada peso!',
    initial: 'M'
  }, {
    name: 'Valentina R.',
    info: 'Instructora de Pilates · Córdoba',
    text: 'Pilates 360 me salvó la vida. Soy instructora hace 3 años y siempre me costó planificar. Con este recurso mis clases tienen estructura, mis alumnas progresan y yo me siento más segura.',
    initial: 'V'
  }, {
    name: 'Camila F.',
    info: 'Profesora de GAP · Rosario',
    text: 'Compré GAP 360 hace dos semanas y ya lo apliqué en todas mis clases. El nivel de detalle es increíble, las progresiones están muy bien pensadas. Súper recomendable.',
    initial: 'C'
  }, {
    name: 'Lucía P.',
    info: 'Instructora de Pole Dance · Mendoza',
    text: 'Lo que más me sorprendió es que podés personalizarlo totalmente. Lo bajé, lo edité con mi nombre y se lo mandé a mis alumnas. Profesional y práctico.',
    initial: 'L'
  }, {
    name: 'Sofía M.',
    info: 'Instructora de Aerial Silks · La Plata',
    text: 'Las progresiones por nivel me ayudaron a estructurar mis grupos de manera mucho más eficiente. Exactamente lo que necesitaba.',
    initial: 'S'
  }, {
    name: 'Andrea G.',
    info: 'Entrenadora de Fútbol Femenino · CABA',
    text: 'Nunca pensé que iba a encontrar algo específico para fútbol femenino. Es muy completo y se nota que lo hizo alguien que sabe.',
    initial: 'A'
  }];
  return <section id="testimonios" className="bg-brand-light py-10 md:py-24" data-id="element-185">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-186">
        <motion.div className="text-center mb-8 md:mb-16" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={staggerContainer} data-id="element-187">
          <motion.span variants={fadeInUp} className="text-brand-green font-bold uppercase tracking-wider text-xs md:text-sm mb-1 md:mb-2 block" data-id="element-188">
            Lo que dicen nuestras instructoras
          </motion.span>
          <motion.h2 variants={fadeInUp} className="text-2xl sm:text-4xl md:text-5xl font-black text-white mb-2 md:mb-4" data-id="element-189">
            Resultados reales
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-sm sm:text-lg text-brand-gray font-medium" data-id="element-190">
            Más de 500 instructoras ya transformaron sus clases
          </motion.p>
        </motion.div>

        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={staggerContainer} data-id="element-191">
          {testimonials.map((test, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-[#1A1A1A] p-5 md:p-8 rounded-2xl shadow-sm border border-brand-border flex flex-col relative hover:shadow-md transition-shadow" data-id="element-192">
              <div className="absolute top-4 right-4 text-brand-green/10 text-6xl font-serif leading-none" data-id="element-193">
                "
              </div>
              <div className="text-yellow-400 text-sm md:text-base mb-3 md:mb-4 tracking-widest relative z-10" data-id="element-194">
                ⭐⭐⭐⭐⭐
              </div>
              <p className="text-brand-gray italic text-sm md:text-base mb-5 md:mb-8 flex-grow relative z-10" data-id="element-195">
                "{test.text}"
              </p>

              <div className="flex items-center gap-3 md:gap-4 mt-auto relative z-10" data-id="element-196">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-green text-white flex items-center justify-center font-bold text-lg md:text-xl flex-shrink-0 ring-4 ring-brand-green/20" data-id="element-197">
                  {test.initial}
                </div>
                <div data-id="element-198">
                  <div className="flex items-center gap-1" data-id="element-199">
                    <h4 className="font-bold text-white text-sm md:text-base" data-id="element-200">
                      {test.name}
                    </h4>
                    <span className="bg-brand-green text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold" data-id="element-201">
                      ✓
                    </span>
                  </div>
                  <p className="text-xs text-brand-gray" data-id="element-202">{test.info}</p>
                </div>
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}
// --- Final CTA Section ---
export function FinalCTA() {
  return <section className="bg-brand-black py-12 md:py-24 relative overflow-hidden" data-id="element-203">
      {/* Subtle radial glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-green/20 rounded-full blur-[100px] pointer-events-none" data-id="element-204"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-205">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={staggerContainer} data-id="element-206">
          <motion.h2 variants={fadeInUp} className="text-3xl sm:text-5xl md:text-6xl font-black text-white mb-4 md:mb-6 leading-tight" data-id="element-207">
            ¿Qué estás esperando para dar{' '}
            <span className="text-brand-green" data-id="element-208">mejores clases</span>?
          </motion.h2>

          <motion.p variants={fadeInUp} className="text-base md:text-xl text-gray-300 mb-8 md:mb-12 font-medium max-w-2xl mx-auto" data-id="element-209">
            Elegí tu disciplina, descargá tu recurso y empezá a transformar tus
            clases desde hoy.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row justify-center items-center gap-3 md:gap-4" data-id="element-210">
            <a href="#productos" className="w-full sm:w-auto bg-brand-green hover:bg-green-600 text-white px-6 md:px-8 py-3.5 md:py-4 rounded-full font-bold text-base md:text-lg transition-colors flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(29,185,84,0.4)] hover:shadow-[0_0_30px_rgba(29,185,84,0.6)]" data-id="element-211">
              🛍️ Ver todos los productos
            </a>
            <a href="https://www.instagram.com/recursosfitness.ar/" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white px-6 md:px-8 py-3.5 md:py-4 rounded-full font-bold text-base md:text-lg transition-colors flex items-center justify-center gap-2" data-id="element-212">
              📲 @recursosfitness.ar
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}